import { createContext, useContext, useRef } from 'react'

import { createStore, useStore } from 'zustand'

/**
 * Which comment the reader had reached in the list, per file.
 *
 * The list is torn down when it closes, and the whole viewer is torn down when
 * another file is opened, so nothing about where the reader was survives in the
 * panel itself. On a review of any size that makes the twentieth comment cost
 * the same scroll every time — and going to a comment, reading its paragraph and
 * coming back to the list is most of what working through a review is.
 *
 * A comment rather than a distance down the list: a row quotes the words its
 * comment is attached to, and can only do that once the document has been laid
 * out that far, so rows grow behind the reader and the same number of pixels
 * stops meaning the same comment.
 *
 * Not persisted. By the next visit to the browser the file's comments will have
 * been answered, resolved and added to.
 */
export interface CommentListPlace {
  /** The comment the top edge of the list cuts through. */
  threadId: string
  /** How much of that comment's row is above the edge, in pixels. */
  above: number
}

interface CommentListPlaceState {
  placeByFile: Record<string, CommentListPlace>
  /**
   * Where the list was left, or null for a file with nothing remembered.
   *
   * Read through the store rather than subscribed to: the list wants this as it
   * opens and gives it back as it closes, and nothing on screen changes in
   * between. A list that re-rendered on every scroll event would stutter under
   * exactly the long review this exists for.
   */
  read: (fileKey: string) => CommentListPlace | null
  remember: (fileKey: string, place: CommentListPlace) => void
}

const createCommentListPlaceStore = () =>
  createStore<CommentListPlaceState>((set, get) => ({
    placeByFile: {},
    read: (fileKey) => get().placeByFile[fileKey] ?? null,
    remember: (fileKey, place) =>
      set((state) => ({
        placeByFile: { ...state.placeByFile, [fileKey]: place },
      })),
  }))

type CommentListPlaceStore = ReturnType<typeof createCommentListPlaceStore>

const CommentListPlaceContext = createContext<CommentListPlaceStore | null>(
  null,
)

export interface CommentListPlaceProviderProps {
  children: React.ReactNode
}

/**
 * One memory of the comment list, for the viewer under it.
 *
 * Above the viewer rather than inside it, since the panel that reads this is
 * mounted only while the list is open. Two viewers on a page — a file open
 * beside a chat and an excerpt in a dialog — remember separately.
 */
export const CommentListPlaceProvider: React.FC<
  CommentListPlaceProviderProps
> = ({ children }) => {
  const store = useRef<CommentListPlaceStore | null>(null)
  if (!store.current) {
    store.current = createCommentListPlaceStore()
  }

  return (
    <CommentListPlaceContext.Provider value={store.current}>
      {children}
    </CommentListPlaceContext.Provider>
  )
}

const useCommentListPlaceStore = <T,>(
  selector: (state: CommentListPlaceState) => T,
): T => {
  const store = useContext(CommentListPlaceContext)
  if (!store) {
    throw new Error(
      'The comment list must be rendered within a CommentListPlaceProvider.',
    )
  }
  return useStore(store, selector)
}

export const useReadCommentListPlace = () =>
  useCommentListPlaceStore((state) => state.read)

export const useRememberCommentListPlace = () =>
  useCommentListPlaceStore((state) => state.remember)
